import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

function FocusGroupParticipants({ user }) {
  const [searchParams] = useSearchParams();
  const [participants, setParticipants] = useState([]);
  const [classCodes, setClassCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, denied: 0 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDevices, setParticipantDevices] = useState([]);
  const [participantSubmissions, setParticipantSubmissions] = useState([]);
  const [participantProgress, setParticipantProgress] = useState(null);
  const [filterClassCode, setFilterClassCode] = useState(searchParams.get('classCode') || '');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchClassCodes();
    fetchParticipants();
  }, [filterClassCode, filterStatus]);

  const fetchClassCodes = async () => {
    try {
      const response = await axios.get('/api/focus-group/class-codes', {
        withCredentials: true,
      });
      setClassCodes(response.data.classCodes || []);
    } catch (error) {
      console.error('Failed to fetch class codes:', error);
    }
  };

  const fetchParticipants = async () => {
    try {
      let url = '/api/focus-group/participants';
      const params = new URLSearchParams();
      if (filterClassCode) params.append('classCode', filterClassCode);
      if (filterStatus) params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        withCredentials: true,
      });
      setParticipants(response.data.participants || []);
      setStats(response.data.stats || { total: 0, approved: 0, pending: 0, denied: 0 });
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      setMessage({ type: 'error', text: 'Failed to load participants. Make sure you\'re logged in.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipantDetails = async (email) => {
    try {
      // Fetch participant details, submissions, and progress in parallel
      const [detailsRes, submissionsRes, progressRes] = await Promise.all([
        axios.get(`/api/focus-group/participants/${encodeURIComponent(email)}`, { withCredentials: true }),
        axios.get(`/api/focus-group/submissions/${encodeURIComponent(email)}`, { withCredentials: true }).catch(() => ({ data: { submissions: [] } })),
        axios.get(`/api/focus-group/progress/${encodeURIComponent(email)}`, { withCredentials: true }).catch(() => ({ data: { progress: null } })),
      ]);

      setSelectedParticipant(detailsRes.data.participant);
      setParticipantDevices(detailsRes.data.devices || []);
      setParticipantSubmissions(submissionsRes.data.submissions || []);
      setParticipantProgress(progressRes.data.progress || null);
    } catch (error) {
      console.error('Failed to fetch participant details:', error);
      setMessage({ type: 'error', text: 'Failed to load participant details' });
    }
  };

  const handleDelete = async (email, name) => {
    if (!window.confirm(`Are you sure you want to remove ${name} (${email})?\n\nThis will revoke their access and delete all their device registrations.`)) {
      return;
    }

    try {
      await axios.delete(`/api/focus-group/participants?email=${encodeURIComponent(email)}`, {
        withCredentials: true,
      });

      setMessage({ type: 'success', text: `Participant '${email}' has been removed.` });
      setSelectedParticipant(null);
      fetchParticipants();
    } catch (error) {
      console.error('Failed to delete participant:', error);
      setMessage({ type: 'error', text: 'Failed to delete participant' });
    }
  };

  const handleStatusChange = async (email, newStatus) => {
    try {
      await axios.patch('/api/focus-group/participants',
        { email, status: newStatus },
        { withCredentials: true }
      );

      setMessage({ type: 'success', text: `Status updated to '${newStatus}'` });
      fetchParticipants();
      if (selectedParticipant?.email === email) {
        fetchParticipantDetails(email);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Approved</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'denied':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Denied</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sage-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link to="/focus-groups" className="text-forest-800 hover:text-forest-700">
                ← Back to Class Codes
              </Link>
              <h1 className="text-2xl font-bold text-forest-800">
                Focus Group Participants
              </h1>
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <select
                value={filterClassCode}
                onChange={(e) => setFilterClassCode(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="">All Classes</option>
                {classCodes.map((cc) => (
                  <option key={cc.code} value={cc.code}>
                    {cc.code} - {cc.className}
                  </option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="denied">Denied</option>
              </select>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      {message && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className={`px-4 py-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-forest-800">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Participants</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-red-600">{stats.denied}</div>
            <div className="text-sm text-gray-600">Denied</div>
          </div>
        </div>

        {/* Participants Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Devices
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {participants.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No participants found. {filterClassCode || filterStatus ? 'Try adjusting your filters.' : 'Students will appear here after using a class code.'}
                  </td>
                </tr>
              ) : (
                participants.map((participant) => (
                  <tr key={participant.email} className={participant.status === 'denied' ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {participant.firstName} {participant.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {participant.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                        {participant.classCode}
                      </code>
                      <div className="text-xs text-gray-500 mt-1">
                        {participant.className || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(participant.requestedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {participant.deviceCount || 0} / {participant.maxDevices || 3}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(participant.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => fetchParticipantDetails(participant.email)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(participant.email, `${participant.firstName} ${participant.lastName}`)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Participant Detail Modal */}
      {selectedParticipant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedParticipant.firstName} {selectedParticipant.lastName}
                  </h2>
                  <p className="text-gray-600">{selectedParticipant.email}</p>
                </div>
                <button
                  onClick={() => setSelectedParticipant(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>

              {/* Status Section */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    {getStatusBadge(selectedParticipant.status)}
                  </div>
                  <div className="flex space-x-2">
                    {selectedParticipant.status !== 'approved' && (
                      <button
                        onClick={() => handleStatusChange(selectedParticipant.email, 'approved')}
                        className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Approve
                      </button>
                    )}
                    {selectedParticipant.status !== 'denied' && (
                      <button
                        onClick={() => handleStatusChange(selectedParticipant.email, 'denied')}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Deny
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Class Code</label>
                  <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                    {selectedParticipant.classCode}
                  </code>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Class Name</label>
                  <p className="text-gray-900">{selectedParticipant.className || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Institution</label>
                  <p className="text-gray-900">{selectedParticipant.institution || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Instructor</label>
                  <p className="text-gray-900">{selectedParticipant.instructorName || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Tier</label>
                  <p className="text-gray-900">{selectedParticipant.tier}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Devices</label>
                  <p className="text-gray-900">{selectedParticipant.deviceCount || 0} / {selectedParticipant.maxDevices || 3}</p>
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Requested</label>
                  <p className="text-gray-900">{formatDateTime(selectedParticipant.requestedAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Approved</label>
                  <p className="text-gray-900">{formatDateTime(selectedParticipant.approvedAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Verified</label>
                  <p className="text-gray-900">{formatDateTime(selectedParticipant.verifiedAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Consent Given</label>
                  <p className="text-gray-900">{formatDateTime(selectedParticipant.consentGivenAt)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Expiration</label>
                  <p className="text-gray-900">{formatDate(selectedParticipant.expirationDate)}</p>
                </div>
              </div>

              {/* Devices List */}
              {participantDevices.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-500 mb-2">Registered Devices</label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    {participantDevices.map((device, idx) => (
                      <div key={idx} className="text-sm text-gray-700 py-1 border-b border-gray-200 last:border-0">
                        <code className="text-xs">{device.deviceId}</code>
                        <span className="text-gray-400 ml-2">({formatDateTime(device.createdAt)})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress Tracking */}
              {participantProgress && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-500 mb-2">Progress Tracking</label>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Journal Sessions:</span>
                        <span className="ml-2 font-semibold text-blue-700">
                          {participantProgress.journalSessionCount || 0} / {participantProgress.requiredSessions || 3}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Consent:</span>
                        <span className={`ml-2 font-semibold ${participantProgress.hasAcceptedConsent ? 'text-green-600' : 'text-gray-400'}`}>
                          {participantProgress.hasAcceptedConsent ? 'Accepted' : 'Not yet'}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Evaluation:</span>
                        <span className={`ml-2 font-semibold ${participantProgress.hasCompletedEvaluation ? 'text-green-600' : 'text-gray-400'}`}>
                          {participantProgress.hasCompletedEvaluation ? 'Completed' : 'Not yet'}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Last Updated:</span>
                        <span className="ml-2 text-sm text-gray-700">
                          {formatDateTime(participantProgress.lastUpdatedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submissions */}
              {participantSubmissions.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Submissions ({participantSubmissions.length})
                  </label>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {participantSubmissions.map((submission) => (
                      <div key={submission.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start mb-2">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            submission.submissionType === 'evaluation'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {submission.submissionType === 'evaluation' ? 'Evaluation' : 'Progress Report'}
                          </span>
                          <span className="text-xs text-gray-500">{formatDateTime(submission.submittedAt)}</span>
                        </div>
                        {submission.submissionType === 'evaluation' && submission.data?.responses && (
                          <div className="mt-2 space-y-2">
                            {submission.data.responses.slice(0, 3).map((response, idx) => (
                              <div key={idx} className="text-xs">
                                <span className="text-gray-600">{response.questionText}:</span>
                                <span className="ml-1 text-gray-900 font-medium">
                                  {response.responseType === 'rating' ? `${response.value}/5` : response.value}
                                </span>
                              </div>
                            ))}
                            {submission.data.responses.length > 3 && (
                              <div className="text-xs text-gray-400">
                                +{submission.data.responses.length - 3} more responses
                              </div>
                            )}
                          </div>
                        )}
                        {submission.submissionType === 'progress_report' && submission.data && (
                          <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <span className="text-gray-600">Days:</span>
                              <span className="ml-1 font-medium">{submission.data.totalDaysJournaled || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Sessions:</span>
                              <span className="ml-1 font-medium">{submission.data.totalSessions || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Avg Words:</span>
                              <span className="ml-1 font-medium">{submission.data.averageWordCount || 0}</span>
                            </div>
                          </div>
                        )}
                        <div className="mt-2 text-xs text-gray-400">
                          App v{submission.appVersion}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setSelectedParticipant(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDelete(selectedParticipant.email, `${selectedParticipant.firstName} ${selectedParticipant.lastName}`)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete Participant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FocusGroupParticipants;
